import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyAssignments, downloadCertificate } from "../services/AssignmentService";
import { launchScorm } from "../services/ScormService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const TYPE_LABEL = {
  "ppt-module": "Module",
  "ppt-item": "Lesson",
  video: "Video",
  assessment: "Assessment",
  "learning-path": "Learning Path",
  scorm: "SCORM",
};

const STATUS_STYLE = {
  assigned: { label: "Not started", bg: "#e9ecef", color: "#495057" },
  "in-progress": { label: "In progress", bg: "#fff3cd", color: "#856404" },
  completed: { label: "Completed", bg: "#d4edda", color: "#155724" },
};

const STEP_DOT = {
  completed: "#28a745",
  "in-progress": "#f0ad4e",
  assigned: "#cbd2d9",
};

/** Clean line-style award/medal icon — professional stand-in for the emoji. */
const AwardIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <circle cx="12" cy="9" r="5.5" />
    <path d="M8.6 13.4 7 21l5-2.6L17 21l-1.6-7.6" />
  </svg>
);

/**
 * "Download Certificate" action for any completed, assigned content. Manages its
 * own busy state and re-verifies (assignment + completion) server-side.
 * `variant="solid"` (default) is a compact gold pill for headers; `"outline"`
 * is a full-width secondary action that pairs cleanly with a primary button.
 */
const CertificateButton = ({ contentType, contentId, title, variant = "solid" }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    const error = await downloadCertificate(contentType, contentId, title);
    setBusy(false);
    if (error) alert(error);
  };

  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: 600,
    borderRadius: "10px",
    cursor: busy ? "wait" : "pointer",
    whiteSpace: "nowrap",
    transition: "background .15s, border-color .15s",
  };
  const solid = {
    ...base,
    padding: "6px 14px",
    color: "#fff",
    border: "1px solid transparent",
    background: busy ? "#a8862a" : "#b8860b",
  };
  const outline = {
    ...base,
    width: "100%",
    padding: "10px",
    color: "#8a6d1a",
    border: "1px solid #e3cd86",
    background: busy ? "#fbf6e6" : "#fdfaf0",
  };

  return (
    <button onClick={handle} disabled={busy} style={variant === "outline" ? outline : solid}>
      <AwardIcon size={variant === "outline" ? 17 : 15} />
      {busy ? "Preparing…" : "Download Certificate"}
    </button>
  );
};

/**
 * A learning-path assignment rendered as a journey: a progress bar plus an
 * ordered checklist of steps, each deep-linking into the existing viewers.
 * Spans the full row so the sequence is readable.
 */
const PathCard = ({ item, openItem, isOverdue }) => {
  const status = STATUS_STYLE[item.status] || STATUS_STYLE.assigned;
  const { done = 0, total = 0 } = item.progress || {};
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div
      style={{
        gridColumn: "1 / -1",
        border: "1px solid #e0e0e0",
        borderRadius: "14px",
        padding: "20px",
        background: "white",
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "12px", background: "#eef2ff", color: "#4e73df", padding: "3px 10px", borderRadius: "12px" }}>
            Learning Path
          </span>
          <h3 style={{ margin: 0, fontWeight: 700 }}>{item.title}</h3>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {item.status === "completed" && (
            <CertificateButton contentType="learning-path" contentId={item.contentId} title={item.title} />
          )}
          <span style={{ fontSize: "12px", background: status.bg, color: status.color, padding: "3px 10px", borderRadius: "12px" }}>
            {status.label}
          </span>
        </div>
      </div>

      {item.dueDate && (
        <p style={{ margin: "8px 0 0", fontSize: "13px", color: isOverdue(item) ? "#c0392b" : "grey", fontWeight: isOverdue(item) ? 600 : 400 }}>
          Due {new Date(item.dueDate).toLocaleDateString()}{isOverdue(item) ? " — overdue" : ""}
        </p>
      )}

      {/* progress bar */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "14px 0" }}>
        <div style={{ flex: 1, height: "8px", background: "#eef0f3", borderRadius: "6px", overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(135deg, rgb(78,115,223), rgb(31,42,122))", transition: "width .3s" }} />
        </div>
        <span style={{ fontSize: "13px", color: "#555", whiteSpace: "nowrap" }}>{done} of {total} done</span>
      </div>

      {/* ordered steps */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {item.steps.map((step, idx) => {
          const sStatus = STATUS_STYLE[step.status] || STATUS_STYLE.assigned;
          return (
            <div
              key={`${step.contentType}-${step.contentId}`}
              style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px", border: "1px solid #eef0f3", borderRadius: "10px" }}
            >
              <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: STEP_DOT[step.status] || STEP_DOT.assigned, flexShrink: 0 }} />
              <span style={{ fontSize: "13px", color: "#888", width: "22px" }}>{idx + 1}.</span>
              <span style={{ fontSize: "11px", background: "#f1f3f5", color: "#555", padding: "2px 8px", borderRadius: "10px" }}>
                {TYPE_LABEL[step.contentType] || step.contentType}
              </span>
              <span style={{ flex: 1, fontSize: "14px" }}>{step.title}</span>
              <span style={{ fontSize: "11px", background: sStatus.bg, color: sStatus.color, padding: "2px 8px", borderRadius: "10px" }}>
                {sStatus.label}
                {step.percent > 0 && step.percent < 100 ? ` · ${step.percent}%` : ""}
              </span>
              <button
                onClick={() => openItem(step)}
                disabled={!step.available}
                style={{
                  background: step.available ? "rgb(78,115,223)" : "#ccc",
                  color: "white", border: "none", borderRadius: "8px",
                  padding: "6px 14px", fontSize: "13px",
                  cursor: step.available ? "pointer" : "not-allowed",
                }}
              >
                {step.available ? "Open" : "N/A"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * "My Learning Plan / IDP" — lists content assigned to the learner. Each item
 * deep-links into the EXISTING viewers (no new viewers): PPT/video open the
 * module section; assessments open within their section. Purely additive to the
 * normal role-based catalog.
 */
export const MyLearning = () => {
  const navigate = useNavigate();
  const userName = localStorage.getItem("userName");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const data = await getMyAssignments();
      setItems(data);
      setLoading(false);
    })();
  }, []);

  const openItem = (item) => {
    if (!item.available) return;
    // SCORM opens in the backend-hosted player (new tab) via a launch ticket —
    // works the same whether it's a standalone item or a step inside a path.
    if (item.contentType === "scorm") {
      launchScorm(item.contentId);
      return;
    }
    // Reuse the existing learner flow: select the section, land on /modules.
    if (item.section) {
      localStorage.setItem("section", item.section);
    }
    // Deep-link to the EXACT assigned content. Without this, /modules just
    // defaults to the first module in the section, so an assigned module
    // appears to "open a different one from the same section".
    localStorage.setItem(
      "openTarget",
      JSON.stringify({
        contentType: item.contentType,
        contentId: item.contentId,
        pptItemId: item.pptItemId,
      })
    );
    navigate("/modules");
  };

  const isOverdue = (item) =>
    item.dueDate &&
    item.status !== "completed" &&
    new Date(item.dueDate) < new Date();

  return (
    <div style={{ padding: "30px 50px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              display: "flex",
              alignItems: "center",
              background: "white",
              border: "1px solid lightblue",
              borderRadius: "10px",
              color: "rgb(78, 115, 223)",
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            <ArrowBackIcon fontSize="small" /> Back
          </button>
          <h2 style={{ margin: 0 }}>My Learning Plan</h2>
        </div>
        <span style={{ color: "grey" }}>{userName}</span>
      </div>

      {loading && <p>Loading your plan…</p>}

      {!loading && items.length === 0 && (
        <div
          style={{
            padding: "40px",
            textAlign: "center",
            color: "grey",
            background: "#f8f9fa",
            borderRadius: "12px",
          }}
        >
          Nothing has been assigned to you yet. Check back soon!
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "20px",
        }}
      >
        {items.map((item) => {
          // Learning paths render as a full-width journey card with steps.
          if (item.contentType === "learning-path") {
            return (
              <PathCard
                key={item._id}
                item={item}
                openItem={openItem}
                isOverdue={isOverdue}
              />
            );
          }
          const status = STATUS_STYLE[item.status] || STATUS_STYLE.assigned;
          return (
            <div
              key={item._id}
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "14px",
                padding: "18px",
                background: "white",
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    background: "#eef2ff",
                    color: "#4e73df",
                    padding: "3px 10px",
                    borderRadius: "12px",
                  }}
                >
                  {TYPE_LABEL[item.contentType] || item.contentType}
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    background: status.bg,
                    color: status.color,
                    padding: "3px 10px",
                    borderRadius: "12px",
                  }}
                >
                  {status.label}
                  {item.percent > 0 && item.percent < 100 ? ` · ${item.percent}%` : ""}
                </span>
              </div>

              <h3 style={{ margin: "4px 0", fontWeight: 600 }}>{item.title}</h3>
              {item.module && (
                <p style={{ margin: 0, color: "grey", fontSize: "13px" }}>
                  Module: {item.module}
                </p>
              )}

              {item.dueDate && (
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: isOverdue(item) ? "#c0392b" : "grey",
                    fontWeight: isOverdue(item) ? 600 : 400,
                  }}
                >
                  Due {new Date(item.dueDate).toLocaleDateString()}
                  {isOverdue(item) ? " — overdue" : ""}
                </p>
              )}

              <div
                style={{
                  marginTop: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <button
                  onClick={() => openItem(item)}
                  disabled={!item.available}
                  style={{
                    width: "100%",
                    background: item.available ? "rgb(78, 115, 223)" : "#ccc",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    padding: "10px",
                    cursor: item.available ? "pointer" : "not-allowed",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {item.available ? "Open" : "Content unavailable"}
                </button>
                {item.status === "completed" && item.contentType === "ppt-module" && (
                  <CertificateButton
                    variant="outline"
                    contentType={item.contentType}
                    contentId={item.contentId}
                    title={item.title}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
